import { Injectable, signal, TemplateRef } from '@angular/core';

export type HeaderActionVariant = 'flat' | 'stroked' | 'icon' | 'fab';

/** A single action button published into the app header bar by a view. */
export interface HeaderAction {
  /** Stable id (used as the *ngFor track key). */
  id: string;
  /** Visible label; omit for icon-only buttons. */
  label?: string;
  /** Optional mat-icon name. */
  icon?: string;
  color?: 'primary' | 'accent' | 'warn';
  /** Button style; defaults to 'flat'. */
  variant?: HeaderActionVariant;
  tooltip?: string;
  disabled?: boolean;
  /** Invoked on click. */
  run: () => void;
}

/** Title + actions a routed view contributes to the shared app header bar. */
export interface PageHeaderConfig {
  title?: string;
  subtitle?: string;
  /**
   * Page glyph rendered next to the title. Either a mat-icon ligature (`settings`) or an
   * `hgi-*` font icon name — use the same icon the menu entry uses for the section.
   */
  icon?: string;
  /** When set, a back arrow button is rendered that invokes this. */
  back?: () => void;
  backTooltip?: string;
  actions?: HeaderAction[];
  /** Show the global date-range selector in the bar (only relevant on date-scoped views). */
  showDateRange?: boolean;
  /**
   * Optional rich content rendered in the bar between the title and the actions (e.g. a
   * dashboard's filter bar). The template is created in the view's context, so its bindings
   * resolve against that view.
   */
  content?: TemplateRef<unknown>;
}

/**
 * Protocol that lets a routed view publish its title and action buttons into the shared app
 * header bar (`wbi-header-bar`) instead of rendering its own header. A view calls {@link set}
 * on entry — typically from an `effect` so labels/disabled stay reactive — and {@link clear} on
 * destroy. The header bar renders {@link config} when present; views that don't set anything
 * leave the bar untouched.
 */
@Injectable({ providedIn: 'root' })
export class PageHeaderService {
  private readonly _config = signal<PageHeaderConfig | null>(null);
  readonly config = this._config.asReadonly();

  set(config: PageHeaderConfig | null): void {
    this._config.set(config);
  }

  clear(): void {
    this._config.set(null);
  }
}
